/**
 * MEMBRE — Déclaration & suivi de réclamation d'assurance (dans /membres).
 * Le membre déclare une blessure/accident, joint des pièces et télécharge le PDF.
 */
import { useEffect, useState, useCallback } from "react";
import { memberApi } from "../lib/api";
import { toast } from "sonner";
import { Shield, Plus, CircleNotch, X, FilePdf, UploadSimple, Paperclip, Trash } from "@phosphor-icons/react";

const STATUS_STYLE = {
  ouvert: "bg-amber-500/15 text-amber-400",
  soumis: "bg-blue-500/15 text-blue-400",
  regle: "bg-green-500/15 text-green-400",
  refuse: "bg-red-500/15 text-red-400",
};

export default function MemberInsuranceSection() {
  const [claims, setClaims] = useState([]);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [waiver, setWaiver] = useState(null);
  const [signing, setSigning] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    memberApi.get("/member/insurance-claims")
      .then((r) => { setClaims(r.data.claims); setIncidentTypes(r.data.incident_types); })
      .catch(() => setClaims([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    memberApi.get("/member/waiver-status").then((r) => setWaiver(r.data)).catch(() => {});
  }, []);

  async function signWaiver() {
    setSigning(true);
    try {
      await memberApi.post("/member/sign-waiver");
      toast.success("Merci ! Ta décharge de responsabilité est signée.");
      setWaiver((w) => ({ ...w, waiver_signed: true, waiver_signed_at: new Date().toISOString() }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Signature impossible."); }
    finally { setSigning(false); }
  }

  const needsAttention = (waiver?.has_contract && !waiver.waiver_signed) || (claims && claims.length > 0);
  if (!loading && !needsAttention && !expanded) {
    return (
      <button onClick={() => setExpanded(true)} data-testid="member-insurance-collapsed"
        className="w-full text-left border border-white/10 hover:border-[#C9A961]/50 bg-white/[0.02] px-5 py-3 mb-8 flex items-center gap-3 text-sm text-white/70">
        <Shield size={16} className="text-[#C9A961]" /> Assurance & incidents — décharge signée ✔ <span className="ml-auto text-xs text-white/45">Déclarer un incident →</span>
      </button>
    );
  }
  return (
    <section className="border border-white/10 bg-white/[0.02] p-6 mb-8" data-testid="member-insurance-section">
      {/* Décharge de responsabilité */}
      {waiver?.has_contract && !waiver.waiver_signed && (
        <div className="mb-5 border border-[#C9A961]/40 bg-[#C9A961]/[0.06] p-4" data-testid="waiver-banner">
          <p className="kz-mono text-[10px] tracking-[0.18em] uppercase text-[#C9A961] mb-1">🛡️ Décharge de responsabilité</p>
          <p className="text-white/75 text-sm mb-3">Tu n'as pas encore signé la décharge de responsabilité du dojo. Elle est requise pour t'entraîner en toute conformité.</p>
          <p className="text-white/50 text-xs mb-3">Je reconnais que la pratique des arts martiaux comporte des risques de blessures, je participe à mes propres risques et je dégage l'Institut Impact Élite Martial de toute responsabilité (sauf faute lourde). Pour un mineur, je signe comme parent/tuteur.</p>
          <button onClick={signWaiver} disabled={signing} data-testid="sign-waiver-btn"
            className="inline-flex items-center gap-2 bg-[#C9A961] text-black font-semibold text-sm px-4 py-2 hover:opacity-90 disabled:opacity-50">
            {signing ? <CircleNotch size={14} className="animate-spin" /> : <Shield size={15} weight="fill" />} Signer ma décharge
          </button>
        </div>
      )}
      {waiver?.has_contract && waiver.waiver_signed && (
        <div className="mb-5 flex items-center gap-2 text-green-400 text-sm" data-testid="waiver-signed-badge">
          <Shield size={16} weight="fill" /> Décharge de responsabilité signée ✅
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h2 className="kz-display text-xl text-white flex items-center gap-2"><Shield size={20} weight="fill" className="text-[#C9A961]" /> Réclamation d'assurance</h2>
        <button onClick={() => setShowForm(true)} data-testid="member-new-claim-btn"
          className="inline-flex items-center gap-2 bg-[#D90429] text-white font-semibold text-sm px-3 py-2 hover:opacity-90">
          <Plus size={15} /> Déclarer un incident
        </button>
      </div>
      <p className="text-white/50 text-sm mb-4">Blessure à l'entraînement ou accident au dojo ? Déclare-le ici, joins tes documents et télécharge le formulaire pour ton assureur.</p>

      {loading ? (
        <CircleNotch size={24} className="animate-spin text-[#C9A961]" />
      ) : claims.length === 0 ? (
        <p className="text-white/40 text-sm" data-testid="member-no-claims">Aucune déclaration pour l'instant.</p>
      ) : (
        <div className="space-y-2" data-testid="member-claims-list">
          {claims.map((c) => (
            <button key={c.id} onClick={() => setDetailId(c.id)} data-testid={`member-claim-${c.id}`}
              className="w-full flex items-center gap-3 border border-white/8 bg-white/[0.02] px-4 py-3 text-left hover:bg-white/[0.04]">
              <span className="kz-mono text-[11px] text-[#C9A961] w-24 shrink-0">{c.claim_number}</span>
              <div className="flex-1 min-w-0">
                <div className="text-white/90 text-sm truncate">{c.incident_label}</div>
                <div className="text-white/40 text-xs">{c.incident_date || "date ?"}</div>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-0.5 ${STATUS_STYLE[c.status] || ""}`}>{c.status_label}</span>
            </button>
          ))}
        </div>
      )}

      {showForm && <MemberClaimForm incidentTypes={incidentTypes} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {detailId && <MemberClaimDetail claimId={detailId} onClose={() => setDetailId(null)} onChanged={load} />}
    </section>
  );
}

function MemberClaimForm({ incidentTypes, onClose, onSaved }) {
  const [f, setF] = useState({
    incident_type: "blessure_entrainement", incident_date: "", incident_time: "",
    discipline: "", description: "", body_part: "", injury_description: "",
    injured: { name: "", phone: "" }, medical: { consulted: false, provider: "" },
    waiver_signed: false,
  });
  const [busy, setBusy] = useState(false);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));

  async function save() {
    if (!f.description.trim()) { toast.error("Décris l'incident, s'il te plaît."); return; }
    setBusy(true);
    try {
      await memberApi.post("/member/insurance-claims", f);
      toast.success("Déclaration enregistrée. Le dojo a été informé.");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible."); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[130] bg-black/80 flex items-start justify-center overflow-y-auto p-4" data-testid="member-claim-form">
      <div className="w-full max-w-lg bg-[#0C0A09] border border-white/15 my-8 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-lg">Déclarer un incident</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-2">
          <div>
            <label className="block text-white/50 text-xs mb-1">Type d'incident</label>
            <select value={f.incident_type} onChange={(e) => up("incident_type", e.target.value)} data-testid="mcf-type"
              className="w-full bg-black/40 border border-white/15 text-white text-sm px-2 py-2">
              {incidentTypes.map((t) => <option key={t.value} value={t.value} className="bg-[#0C0A09]">{t.label}</option>)}
            </select>
          </div>
          <MInp label="Discipline" value={f.discipline} onChange={(v) => up("discipline", v)} testid="mcf-discipline" />
          <MInp label="Date" value={f.incident_date} onChange={(v) => up("incident_date", v)} testid="mcf-date" type="date" />
          <MInp label="Heure" value={f.incident_time} onChange={(v) => up("incident_time", v)} testid="mcf-time" type="time" />
        </div>
        <MTxt label="Que s'est-il passé ? *" value={f.description} onChange={(v) => up("description", v)} testid="mcf-description" />
        <div className="grid grid-cols-2 gap-3">
          <MInp label="Partie du corps atteinte" value={f.body_part} onChange={(v) => up("body_part", v)} testid="mcf-bodypart" />
          <MInp label="Téléphone" value={f.injured.phone} onChange={(v) => setF((p) => ({ ...p, injured: { ...p.injured, phone: v } }))} testid="mcf-phone" />
        </div>
        <MTxt label="Nature de la blessure" value={f.injury_description} onChange={(v) => up("injury_description", v)} testid="mcf-injury" />
        <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer mt-2 mb-2" data-testid="mcf-consulted-label">
          <input type="checkbox" checked={f.medical.consulted} onChange={(e) => setF((p) => ({ ...p, medical: { ...p.medical, consulted: e.target.checked } }))} data-testid="mcf-consulted" />
          J'ai consulté un médecin / une clinique
        </label>
        {f.medical.consulted && (
          <MInp label="Établissement / médecin" value={f.medical.provider} onChange={(v) => setF((p) => ({ ...p, medical: { ...p.medical, provider: v } }))} testid="mcf-provider" />
        )}
        <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer mt-2 p-2 border border-[#C9A961]/30 bg-[#C9A961]/[0.05]" data-testid="mcf-waiver-label">
          <input type="checkbox" checked={!!f.waiver_signed} onChange={(e) => up("waiver_signed", e.target.checked)} data-testid="mcf-waiver" />
          🛡️ J'ai signé la décharge de responsabilité du dojo
        </label>
        <div className="flex gap-2 mt-4">
          <button onClick={save} disabled={busy} data-testid="mcf-save"
            className="flex-1 bg-[#C9A961] text-black font-semibold py-2.5 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {busy ? <CircleNotch size={15} className="animate-spin" /> : "Envoyer la déclaration"}
          </button>
          <button onClick={onClose} className="border border-white/15 text-white/70 px-4 py-2.5">Annuler</button>
        </div>
        <p className="text-white/35 text-[11px] mt-3">Tu pourras joindre des pièces (photos, rapport médical) après l'envoi.</p>
      </div>
    </div>
  );
}

function MemberClaimDetail({ claimId, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    memberApi.get(`/member/insurance-claims/${claimId}`).then((r) => setC(r.data.claim)).catch(() => onClose());
  }, [claimId, onClose]);
  useEffect(() => { load(); }, [load]);

  async function upload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("category", "Pièce jointe");
      await memberApi.post(`/member/insurance-claims/${claimId}/upload`, fd);
      toast.success("Pièce ajoutée."); load(); onChanged();
    } catch (er) { toast.error("Upload impossible."); }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function downloadPdf() {
    try {
      const r = await memberApi.get(`/member/insurance-claims/${claimId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `reclamation-${c?.claim_number || claimId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (er) { toast.error("PDF impossible."); }
  }

  if (!c) return null;
  return (
    <div className="fixed inset-0 z-[130] bg-black/80 flex items-start justify-center overflow-y-auto p-4" data-testid="member-claim-detail" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#0C0A09] border border-white/15 my-8 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-semibold text-lg">{c.claim_number}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-white/50 text-sm mb-4">{c.incident_label} · {c.incident_date || "date ?"} · <span className={`px-2 py-0.5 ${STATUS_STYLE[c.status] || ""}`}>{c.status_label}</span></p>
        <div className="mb-3">
          <div className="text-white/40 text-xs mb-1">Description</div>
          <div className="text-white/80 text-sm whitespace-pre-wrap">{c.description || "—"}</div>
        </div>

        <h4 className="text-[#C9A961] text-sm font-semibold mt-4 mb-2 flex items-center gap-2"><Paperclip size={15} /> Pièces jointes</h4>
        <div className="space-y-1.5 mb-3">
          {(c.attachments || []).length === 0 && <p className="text-white/40 text-xs">Aucune pièce jointe.</p>}
          {(c.attachments || []).map((a) => (
            <div key={a.id} className="flex items-center gap-2 border border-white/8 px-3 py-1.5 text-xs">
              <a href={a.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-white/80 hover:text-[#C9A961]">{a.filename || a.url}</a>
              <span className="text-white/40">{a.date}</span>
            </div>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 border border-white/15 text-white/80 text-sm px-3 py-2 hover:bg-white/[0.05] cursor-pointer" data-testid="member-upload-attachment">
          {uploading ? <CircleNotch size={14} className="animate-spin" /> : <UploadSimple size={14} />} Ajouter une pièce
          <input type="file" className="hidden" onChange={upload} disabled={uploading} />
        </label>

        <div className="mt-5 pt-4 border-t border-white/8">
          <button onClick={downloadPdf} data-testid="member-download-pdf"
            className="inline-flex items-center gap-2 bg-[#D90429] text-white font-semibold text-sm px-4 py-2.5 hover:opacity-90">
            <FilePdf size={16} /> Télécharger mon formulaire (PDF)
          </button>
        </div>
      </div>
    </div>
  );
}

function MInp({ label, value, onChange, testid, type = "text" }) {
  return (
    <div className="mb-2">
      <label className="block text-white/50 text-xs mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-black/40 border border-white/15 text-white text-sm px-3 py-2" />
    </div>
  );
}

function MTxt({ label, value, onChange, testid }) {
  return (
    <div className="mb-2">
      <label className="block text-white/50 text-xs mb-1">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={3}
        className="w-full bg-black/40 border border-white/15 text-white text-sm px-3 py-2 resize-y" />
    </div>
  );
}
